import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop component - Scroll management + SPA page_view tracking
 *
 * Behavior:
 * - ALL navigation (push, replace, back/forward) scrolls to top
 * - Fires a GA4 page_view event on every route change so single-page
 *   navigations are counted in Google Analytics (fixes the issue where
 *   GSC reports 36 organic clicks but GA4 only registered 6 sessions).
 *
 * GA4 consent is enforced by GTM's Consent Mode (configured in
 * public/index.html). If analytics_storage is "denied", the event is
 * automatically gated by Consent Mode and not sent.
 */
function ScrollToTop() {
  const { pathname, search, key } = useLocation();
  const prevPathRef = useRef(pathname);

  useEffect(() => {
    if (pathname !== prevPathRef.current) {
      window.scrollTo(0, 0);

      // Fire GA4 SPA page_view. Delayed one frame so react-helmet can
      // update document.title before the event is sent.
      //
      // `send_to` routes the event directly to our GA4 property,
      // independent of the GTM container's tag configuration. Without
      // this, SPA page_views land in dataLayer and only reach GA4 if a
      // GTM tag is explicitly configured to forward them — which we
      // can't guarantee from the codebase. Defensive belt-and-suspenders.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        requestAnimationFrame(() => {
          window.gtag("event", "page_view", {
            send_to: "G-Y1W8JJ74C8",
            page_path: pathname + (search || ""),
            page_location: window.location.href,
            page_title: document.title,
          });
        });
      }
    }

    prevPathRef.current = pathname;
  }, [pathname, search, key]);

  return null;
}

export default ScrollToTop;
